import posthog from "posthog-js";
import * as Sentry from "@sentry/react";

// ---------------------------------------------------------------------------
// GTM dataLayer type declaration
// ---------------------------------------------------------------------------
declare global {
	interface Window {
		dataLayer?: Record<string, unknown>[];
	}
}

// ---------------------------------------------------------------------------
// Event property types
// ---------------------------------------------------------------------------

interface SignupFunnelChaptersReadyProps {
	chapter_count: number;
	storage_file_id: string | null;
}

interface SignupFunnelExtractionFailedProps {
	storage_file_id: string | null;
	error_message: string;
}

interface SignupFunnelCompletedProps {
	signup_method: "email" | "google";
	chapter_count: number;
	storage_file_id: string | null;
}

interface SignupFunnelFormViewedProps {
	chapter_count: number;
	storage_file_id: string | null;
}

interface SignupFunnelExtractionStartedProps {
	storage_file_id: string | null;
	/** 1-based attempt counter (increments on retries) */
	attempt: number;
}

interface SignupFunnelExtractionRetryProps {
	storage_file_id: string | null;
	/** 1-based attempt counter of the retry being started */
	attempt: number;
}

interface SignupFunnelFallbackSignupProps {
	storage_file_id: string | null;
}

interface SignupFunnelFormStartedProps {
	storage_file_id: string | null;
}

interface SignupFunnelDetailsSubmittedProps {
	signup_method: "email" | "google";
	chapter_count: number;
	storage_file_id: string | null;
	extraction_status: "idle" | "running" | "done" | "failed";
}

interface CheckoutInitiatedProps {
	price_id: string;
	action: "checkout" | "upgrade" | "downgrade";
	plan_name?: string;
}

interface SubscriptionPurchasedProps {
	session_id?: string | null;
	price_id?: string;
	plan_name?: string;
	amount?: number;
	currency?: string;
}

interface SubscriptionUpgradedProps {
	price_id: string;
	plan_name?: string;
}

interface SubscriptionDowngradedProps {
	price_id: string;
	plan_name?: string;
}

interface SubscriptionCanceledProps {
	plan_name?: string;
}

interface PlanViewedProps {
	plan_name?: string;
	price_id?: string;
	price?: number;
	currency?: string;
}

// -- Pricing / plan selection --

interface PricingModalOpenedProps {
	/** Where the modal was opened from, e.g. "workspace" */
	source: string;
}

interface PricingModalClosedProps {
	/** Where the modal was opened from, e.g. "workspace" */
	source: string;
}

interface TrialCapUpgradeModalViewedProps {
	remaining_coins?: number;
	window_reset_at?: string;
}

interface OnboardingPlanSelectedProps {
	/** Which plan the user picked, e.g. "creator" | "starter" */
	plan: string;
}

interface ActivationUpgradePromptProps {
	podcast_id?: string;
}

interface ActivationUpgradePromptClickedProps
	extends ActivationUpgradePromptProps {
	action: "view_plans" | "dismiss";
}

interface UpgradeNudgeProps {
	nudge_id: string;
	trigger?: string;
	podcast_id?: string;
	podcast_count?: number;
	minutes_listened?: number;
	coin_balance?: number;
	missing_coins?: number;
	required_coins?: number;
}

// -- User lifecycle --

interface UserSignedUpProps {
	method: "email" | "google" | "apple";
}

// -- Document --

interface DocumentUploadedProps {
	file_type?: string;
	file_size_mb?: number;
	title?: string;
	chapter_count?: number;
}

interface CostEstimateFailedProps {
	/** HTTP status of the failed estimate call, if it was an HTTP error */
	status?: number;
	chapter_count?: number;
}

// -- Podcast --

interface PodcastCreatedProps {
	podcast_id?: string;
	workspace_id?: string;
	podcast_length?: string;
	language?: string;
	title?: string;
}

// -- Chat --

interface ChatMessageSentProps {
	chat_id?: string;
	workspace_id?: string;
	connected_workspace_count?: number;
}

// -- Transcript --

interface TranscriptOpenedProps {
	platform: "mobile" | "desktop";
	podcast_id?: string | null;
}

// -- Listening --

interface ListeningMilestoneProps {
	podcast_id: string;
	/** Which milestone was reached: 20 or 90 */
	milestone: 20 | 90;
	current_time: number;
	duration: number;
	/** Total actually-listened seconds (from segments) */
	listened_seconds?: number;
}

// -- Habit Loop v1 --

interface ContinueListeningProps {
	podcast_id?: string;
}

interface CreateNextPromptProps {
	source: "dashboard" | "post_listen";
}

interface ShareSignupCtaProps {
	share_token?: string;
	context: "podcast" | "chapter";
}

interface PostListenPromptProps {
	podcast_id?: string;
	action?: "create_next" | "open_chat";
}

interface TrialBannerVariantProps {
	variant: "rich" | "generic";
}

// ---------------------------------------------------------------------------
// Event map – add new events here to get full type-safety
// ---------------------------------------------------------------------------

interface AnalyticsEventMap {
	// Habit Loop v1
	continue_listening_shown: ContinueListeningProps;
	continue_listening_clicked: ContinueListeningProps;
	create_next_prompt_shown: CreateNextPromptProps;
	create_next_prompt_clicked: CreateNextPromptProps;
	share_signup_cta_shown: ShareSignupCtaProps;
	share_signup_cta_clicked: ShareSignupCtaProps;
	share_signup_cta_dismissed: ShareSignupCtaProps;
	post_listen_prompt_shown: PostListenPromptProps;
	post_listen_prompt_clicked: PostListenPromptProps;
	trial_banner_variant_shown: TrialBannerVariantProps;

	// Signup funnel
	signup_funnel_extraction_started: SignupFunnelExtractionStartedProps;
	signup_funnel_chapters_ready: SignupFunnelChaptersReadyProps;
	signup_funnel_extraction_failed: SignupFunnelExtractionFailedProps;
	signup_funnel_extraction_retry: SignupFunnelExtractionRetryProps;
	signup_funnel_fallback_signup: SignupFunnelFallbackSignupProps;
	signup_funnel_form_viewed: SignupFunnelFormViewedProps;
	signup_funnel_form_started: SignupFunnelFormStartedProps;
	signup_funnel_details_submitted: SignupFunnelDetailsSubmittedProps;
	signup_funnel_completed: SignupFunnelCompletedProps;

	// User lifecycle
	user_signed_up: UserSignedUpProps;

	// Document
	document_uploaded: DocumentUploadedProps;
	cost_estimate_failed: CostEstimateFailedProps;

	// Podcast
	podcast_created: PodcastCreatedProps;

	// Chat
	chat_message_sent: ChatMessageSentProps;

	// Transcript
	transcript_opened: TranscriptOpenedProps;

	// Listening
	listening_milestone: ListeningMilestoneProps;

	// Subscription / conversion
	checkout_initiated: CheckoutInitiatedProps;
	subscription_purchased: SubscriptionPurchasedProps;
	subscription_upgraded: SubscriptionUpgradedProps;
	subscription_downgraded: SubscriptionDowngradedProps;
	subscription_canceled: SubscriptionCanceledProps;
	plan_viewed: PlanViewedProps;

	// Pricing / plan selection
	pricing_modal_opened: PricingModalOpenedProps;
	pricing_modal_closed: PricingModalClosedProps;
	trial_cap_upgrade_modal_viewed: TrialCapUpgradeModalViewedProps;
	onboarding_plan_selected: OnboardingPlanSelectedProps;
	activation_upgrade_prompt_shown: ActivationUpgradePromptProps;
	activation_upgrade_prompt_clicked: ActivationUpgradePromptClickedProps;
	upgrade_nudge_shown: UpgradeNudgeProps;
	upgrade_nudge_clicked: UpgradeNudgeProps;
	upgrade_nudge_dismissed: UpgradeNudgeProps;
}

// Allow arbitrary custom events with a loose fallback
type EventName = keyof AnalyticsEventMap;

// ---------------------------------------------------------------------------
// Analytics Service
// ---------------------------------------------------------------------------

/**
 * Centralised analytics service that fans out events to **PostHog** and
 * **Google Tag Manager** (via `window.dataLayer`).
 *
 * Usage from anywhere (services, components, hooks):
 * ```ts
 * import { AnalyticsService } from "@/lib/analytics/analytics.service";
 *
 * AnalyticsService.track("checkout_initiated", {
 *   price_id: "price_xxx",
 *   action: "checkout",
 * });
 * ```
 */
export class AnalyticsService {
	// ------------------------------------------------------------------
	// Event queue – buffer events fired before PostHog is ready
	// ------------------------------------------------------------------

	private static eventQueue: Array<{
		event: string;
		properties?: Record<string, unknown>;
	}> = [];
	private static queueFlushed = false;

	/**
	 * Flush any buffered events once PostHog is ready.
	 * Call this after PostHog has been initialised (e.g. inside the provider).
	 */
	static flushQueue(): void {
		if (AnalyticsService.queueFlushed) return;
		AnalyticsService.queueFlushed = true;

		const queue = AnalyticsService.eventQueue;
		AnalyticsService.eventQueue = [];

		for (const { event, properties } of queue) {
			AnalyticsService.trackPostHog(event, properties);
		}
	}

	// ------------------------------------------------------------------
	// Core tracking
	// ------------------------------------------------------------------

	/**
	 * Track a typed analytics event. Sends to both PostHog and GTM dataLayer.
	 */
	static track<E extends EventName>(
		event: E,
		properties: AnalyticsEventMap[E],
	): void;
	/**
	 * Track a custom / untyped event.
	 */
	static track(event: string, properties?: Record<string, unknown>): void;
	static track(event: string, properties?: Record<string, unknown>): void {
		AnalyticsService.trackPostHog(event, properties);
		AnalyticsService.trackGTM(event, properties);
	}

	// ------------------------------------------------------------------
	// PostHog
	// ------------------------------------------------------------------

	private static isPostHogReady(): boolean {
		try {
			return !!(
				posthog &&
				typeof posthog.capture === "function" &&
				(posthog as any).__loaded
			);
		} catch {
			return false;
		}
	}

	private static trackPostHog(
		event: string,
		properties?: Record<string, unknown>,
	): void {
		try {
			if (AnalyticsService.isPostHogReady()) {
				posthog.capture(event, properties);
			} else if (!AnalyticsService.queueFlushed) {
				// PostHog not ready yet – buffer the event
				AnalyticsService.eventQueue.push({ event, properties });
			}
			// If queue already flushed but posthog still not ready, drop silently
			// (posthog was likely disabled)
		} catch (error) {
			Sentry.captureException(error, {
				tags: { subsystem: "analytics", provider: "posthog" },
				extra: { event, properties },
			});
		}
	}

	// ------------------------------------------------------------------
	// Google Tag Manager (dataLayer)
	// ------------------------------------------------------------------

	private static trackGTM(
		event: string,
		properties?: Record<string, unknown>,
	): void {
		try {
			window.dataLayer = window.dataLayer ?? [];
			window.dataLayer.push({
				event,
				eventProperties: properties ?? {},
			});
		} catch (error) {
			Sentry.captureException(error, {
				tags: { subsystem: "analytics", provider: "gtm" },
				extra: { event, properties },
			});
		}
	}

	// ------------------------------------------------------------------
	// Identity helpers
	// ------------------------------------------------------------------

	/**
	 * Identify the current user in PostHog and GTM (call after login / signup).
	 */
	static identify(userId: string, traits?: Record<string, unknown>): void {
		try {
			if (posthog && typeof posthog.identify === "function") {
				posthog.identify(userId, traits);
			}
			// Push user identity to GTM so GA4 / Google Ads can use it
			window.dataLayer = window.dataLayer ?? [];
			window.dataLayer.push({
				event: "identify",
				userId,
				userTraits: traits ?? {},
			});
		} catch (error) {
			Sentry.captureException(error, {
				tags: { subsystem: "analytics", method: "identify" },
				extra: { userId },
			});
		}
	}

	/**
	 * Habit Loop v1 - if the current URL carries `via=share`, persist the
	 * acquisition source on the person profile (set-once so the first touch
	 * wins). Call this on signup completion.
	 */
	static captureAcquisitionFromUrl(): void {
		try {
			const params = new URLSearchParams(window.location.search);
			if (params.get("via") !== "share") return;

			const props: Record<string, unknown> = {
				acquisition_source: "share_link",
			};
			const token = params.get("token");
			if (token) props.acquisition_share_token = token;

			const ph = posthog as unknown as {
				setPersonProperties?: (
					set?: Record<string, unknown>,
					setOnce?: Record<string, unknown>,
				) => void;
			};
			if (typeof ph.setPersonProperties === "function") {
				// Second arg is $set_once: first acquisition touch wins.
				ph.setPersonProperties({}, props);
			}
		} catch (error) {
			Sentry.captureException(error, {
				tags: { subsystem: "analytics", method: "captureAcquisitionFromUrl" },
			});
		}
	}

	/**
	 * Reset the current user identity (call on logout).
	 */
	static reset(): void {
		try {
			if (posthog && typeof posthog.reset === "function") {
				posthog.reset();
			}
		} catch (error) {
			Sentry.captureException(error, {
				tags: { subsystem: "analytics", method: "reset" },
			});
		}
	}

	// ------------------------------------------------------------------
	// Page view
	// ------------------------------------------------------------------

	/**
	 * Track a manual page view in PostHog + GTM.
	 */
	static pageView(url?: string): void {
		const props: Record<string, unknown> = {};
		if (url) props.url = url;

		AnalyticsService.trackPostHog("$pageview", props);
		AnalyticsService.trackGTM("page_view", props);
	}
}
